import { Injectable } from '@angular/core';
import Dexie, { liveQuery, Table } from 'dexie';
import { from, Observable } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import {
  PendingValidation,
  PendingValidationInput,
  PendingValidationStatus,
  TicketCacheRow,
  ValidationCacheItem,
} from './types';

/**
 * C2-FRO-002 Fase 2 — Persistencia local (IndexedDB via Dexie) del cache de
 * tickets activos + queue de validaciones pendientes de sync.
 *
 * Diseño:
 *   - Tabla `tickets_cache`: snapshot pre-fetched al iniciar turno. PK por
 *     ticket.code. TTL configurable (default 24h). Se descarta filas vencidas.
 *   - Tabla `pending_validations`: cola FIFO de validaciones hechas offline.
 *     Cada item arranca con `idempotency_key` (UUID v4) que NUNCA se regenera
 *     entre retries — esto permite que C2-SEC-002 (UPDATE atómico server-side)
 *     deduplique correctamente.
 *
 * IndexedDB schema migrations (futuro):
 *   - Cualquier cambio de schema requiere bump de `version()` y un upgrade
 *     callback. NUNCA modificar version 1 retroactivamente — usuarios con
 *     data en v1 quedarían con DB corrupta.
 *   - Ejemplo de upgrade futuro:
 *       db.version(2).stores({
 *         tickets_cache: '&code, function_id, expires_at, [function_id+expires_at]',
 *       }).upgrade(async tx => { ... });
 *
 * Limitaciones conocidas:
 *   - Quota IndexedDB en Safari iOS puede ser tan baja como 50MB con eviction
 *     silenciosa. Si la cola crece descontroladamente, considerar alerta cuando
 *     `pending_validations.count() > 500`.
 *   - `crypto.randomUUID()` requiere contexto seguro (HTTPS / localhost). En
 *     contextos no seguros se usa fallback con la lib `uuid` (ya en deps).
 */

const DB_NAME = 'goliiive_validation_v1';
const TICKET_CACHE_DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;  // 24h

class ValidationDB extends Dexie {
  tickets_cache!: Table<TicketCacheRow, string>;
  pending_validations!: Table<PendingValidation, number>;

  constructor() {
    super(DB_NAME);
    this.version(1).stores({
      // PK code + índices function_id, expires_at, cached_at
      tickets_cache: '&code, function_id, expires_at, cached_at',
      // PK auto-increment, índices ticket_code, function_id, status, created_at
      pending_validations: '++id, ticket_code, function_id, status, created_at, idempotency_key',
    });
  }
}

@Injectable({ providedIn: 'root' })
export class OfflineValidationService {
  private readonly db: ValidationDB = new ValidationDB();

  // ─────────────────────────────────────────────────────────────
  // Cache de tickets (pre-fetch al iniciar turno)
  // ─────────────────────────────────────────────────────────────

  /**
   * Inserta los tickets devueltos por `GET /validation-cache` en IndexedDB.
   * Usa bulkPut → upsert atómico, si ya hay un row con mismo `code` lo
   * sobrescribe (refresca el snapshot).
   *
   * @returns cantidad de filas escritas.
   */
  async prefetchFunctionTickets(
    functionId: number,
    tickets: ValidationCacheItem[],
    ttlMs: number = TICKET_CACHE_DEFAULT_TTL_MS,
  ): Promise<number> {
    const now = Date.now();
    const rows: TicketCacheRow[] = tickets.map((t) => ({
      code: t.code,
      is_used: t.is_used,
      zone_id: t.zone_id,
      seat_id: t.seat_id,
      function_id: functionId,
      cached_at: now,
      expires_at: now + ttlMs,
    }));
    await this.db.tickets_cache.bulkPut(rows);
    return rows.length;
  }

  /**
   * Busca un ticket en cache. Devuelve undefined si no existe o si está
   * vencido (se ignora silenciosamente sin borrar — cleanupExpired() lo hará).
   */
  async getCachedTicket(code: string): Promise<TicketCacheRow | undefined> {
    const row = await this.db.tickets_cache.get(code);
    if (!row) return undefined;
    if (row.expires_at < Date.now()) return undefined;
    return row;
  }

  /** Total de tickets en cache para una función específica (cheap COUNT). */
  async countCachedTicketsByFunction(functionId: number): Promise<number> {
    return this.db.tickets_cache.where('function_id').equals(functionId).count();
  }

  /** Borra rows con expires_at < now. Llamar en bootstrap. */
  async cleanupExpired(): Promise<number> {
    const now = Date.now();
    const deleted = await this.db.tickets_cache.where('expires_at').below(now).delete();
    return deleted;
  }

  /**
   * Recovery de rows huérfanos en estado `syncing`. Si el tab murió (crash,
   * cierre forzado, kill task) mid-process del SyncOrchestrator, el row queda
   * con `status='syncing'` y `getPendingQueue` lo ignora (solo filtra pending),
   * resultando en validaciones perdidas.
   *
   * Debe llamarse en bootstrap, ANTES de `SyncOrchestrator.initialize()`. Es
   * seguro repetir el replay porque cada row mantiene su `idempotency_key`
   * original → C2-SEC-002 (UPDATE atómico AND is_used=0) dedupe si el row
   * llegó a aplicarse server-side antes del crash.
   *
   * Devuelve cantidad de rows rescatados.
   */
  async recoverOrphanedSyncing(): Promise<number> {
    return await this.db.pending_validations
      .where('status')
      .equals('syncing')
      .modify({ status: 'pending' as PendingValidationStatus });
  }

  // ─────────────────────────────────────────────────────────────
  // Cola de validaciones pendientes
  // ─────────────────────────────────────────────────────────────

  /**
   * Genera un nuevo idempotency_key con crypto.randomUUID() si está disponible
   * (contexto seguro) o con la lib `uuid` v4 como fallback.
   */
  private generateIdempotencyKey(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return uuidv4();
  }

  /**
   * Encola una validación offline. Genera idempotency_key único (UUID v4) que
   * permanece en el row para todos los retries. Devuelve el id local generado.
   */
  async enqueueValidation(payload: PendingValidationInput): Promise<number> {
    const row: PendingValidation = {
      ...payload,
      idempotency_key: this.generateIdempotencyKey(),
      created_at: Date.now(),
      status: 'pending',
      retry_count: 0,
    };
    const id = await this.db.pending_validations.add(row);
    return Number(id);
  }

  /** Cola FIFO de items con status pending o failed con next_retry_at vencido. */
  async getPendingQueue(): Promise<PendingValidation[]> {
    const now = Date.now();
    const all = await this.db.pending_validations
      .where('status')
      .anyOf(['pending'])
      .sortBy('created_at');
    return all.filter((item) => !item.next_retry_at || item.next_retry_at <= now);
  }

  /**
   * Observable reactivo con count de pending + failed (no incluye syncing
   * ni conflict ni failed_max_retries). Útil para UI banners.
   */
  getPendingCount$(): Observable<number> {
    return from(
      liveQuery(() =>
        this.db.pending_validations
          .where('status')
          .anyOf(['pending', 'syncing'])
          .count(),
      ),
    );
  }

  /** Cambia el status de un item (mutex compartido entre componente y orchestrator). */
  async markStatus(id: number, status: PendingValidationStatus): Promise<void> {
    await this.db.pending_validations.update(id, { status });
  }

  /**
   * Marca el item como sincronizado exitosamente. Por design BORRA el row para
   * no acumular synced indefinidamente. Audit trail vive server-side en
   * `experience.exit_control` + `experience.ticket.is_used`.
   */
  async markValidationSynced(id: number): Promise<void> {
    await this.db.pending_validations.delete(id);
  }

  /** 409 cross-puerta: deja el row como conflict para inspección admin. */
  async markValidationConflict(id: number, errorPayload: unknown): Promise<void> {
    await this.db.pending_validations.update(id, {
      status: 'conflict',
      last_error: typeof errorPayload === 'string' ? errorPayload : JSON.stringify(errorPayload),
    });
  }

  /**
   * 5xx o network: registra retry pendiente con next_retry_at en el futuro.
   * El orchestrator chequea next_retry_at antes de procesar.
   */
  async markValidationFailed(
    id: number,
    error: string,
    nextRetryAtMs: number,
  ): Promise<void> {
    const existing = await this.db.pending_validations.get(id);
    if (!existing) return;
    await this.db.pending_validations.update(id, {
      status: 'pending',
      last_error: error,
      retry_count: (existing.retry_count ?? 0) + 1,
      next_retry_at: nextRetryAtMs,
    });
  }

  /** Si retry_count > 10, marca permanente y requiere intervención manual. */
  async markValidationFailedMaxRetries(id: number, error: string): Promise<void> {
    await this.db.pending_validations.update(id, {
      status: 'failed_max_retries',
      last_error: error,
    });
  }

  /** 4xx distinto de 409 (ticket inválido, porter_code malo): no retry. */
  async markValidationFailedPermanent(id: number, error: string): Promise<void> {
    await this.db.pending_validations.update(id, {
      status: 'failed',
      last_error: error,
    });
  }

  /** Total de items en cualquier estado para una función. UX del admin. */
  async countByFunction(functionId: number): Promise<number> {
    return this.db.pending_validations.where('function_id').equals(functionId).count();
  }
}
